/**
 * Niet in het origineel: de route om alles onder `/admin` heen. Zonder sessie verschijnt
 * het inlogscherm op dezelfde URL; met sessie de gevraagde pagina, met de spelleider in
 * `useSpelleider()`.
 *
 * Het spelscherm (`/admin/:gameId`) zit hier ook onder. Zo opent de WebSocket pas na het
 * inloggen: de server geeft de verbinding alleen spelleiderrechten als de sessiecookie er
 * bij het verbinden al is. Verloopt de sessie tijdens het spel, dan verschijnt hier het
 * inlogscherm en sluit de verbinding; na het inloggen komt er een nieuwe, met rechten.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Center, Heading, Text, VStack } from '@chakra-ui/react';
import { Outlet } from 'react-router';
import type { Spelleider } from '@ame/shared';
import { Laadscherm } from '@/components/Laadscherm';
import {
  ikOphalen,
  meldSessieVerlopen,
  opSessieVerlopen,
  uitloggen as uitloggenOpServer,
} from '@/spel/auth-api';
import { tekst } from './hulp';
import { Inlogscherm } from './Inloggen';
import { SessieContext, type Sessie } from './sessie';

type Stand =
  | { soort: 'laden' }
  | { soort: 'onbereikbaar'; melding: string }
  /** `verlopen`: de sessie verliep terwijl de pagina open stond. */
  | { soort: 'uit'; verlopen: boolean }
  | { soort: 'in'; spelleider: Spelleider };

/** Zo lang mag het opvragen duren voor er een lader verschijnt; meestal is het sneller. */
const LADER_NA = 300;

/**
 * Zo vaak vraagt een open pagina wie er is ingelogd. Tijdens een spel praat het adminscherm
 * alleen over de WebSocket; zonder dit verliep de sessie dan een uur na het laatste verzoek
 * (`SESSIE_STIL_TTL`). Elk verzoek schuift die tijd op. Na twaalf uur is het toch klaar.
 */
const SESSIE_VERVERSEN = 5 * 60_000;

export function AlleenSpelleider() {
  const [stand, setStand] = useState<Stand>({ soort: 'laden' });

  const laad = useCallback(async () => {
    try {
      const spelleider = await ikOphalen();
      setStand(spelleider ? { soort: 'in', spelleider } : { soort: 'uit', verlopen: false });
    } catch (fout) {
      setStand({ soort: 'onbereikbaar', melding: tekst(fout) });
    }
  }, []);

  useEffect(() => {
    void laad();
  }, [laad]);

  // Een 401 van een route van de spelleider: de sessie is verlopen of ingetrokken. Ook het
  // spel meldt dat, als het een actie van de spelleider weigert (zie `SpelProvider`).
  useEffect(
    () =>
      opSessieVerlopen(() =>
        setStand((nu) => (nu.soort === 'in' ? { soort: 'uit', verlopen: true } : nu)),
      ),
    [],
  );

  // Niet in het origineel: de sessie levend houden zolang de pagina open is.
  const ingelogd = stand.soort === 'in';
  useEffect(() => {
    if (!ingelogd) return;
    const timer = setInterval(() => {
      ikOphalen()
        .then((ik) => {
          if (!ik) meldSessieVerlopen();
        })
        .catch(() => {
          // Niet bereikbaar: de volgende keer opnieuw.
        });
    }, SESSIE_VERVERSEN);
    return () => clearInterval(timer);
  }, [ingelogd]);

  const uitloggen = useCallback(async () => {
    await uitloggenOpServer();
    setStand({ soort: 'uit', verlopen: false });
  }, []);

  const spelleider = stand.soort === 'in' ? stand.spelleider : null;
  const sessie = useMemo<Sessie | null>(
    () => (spelleider ? { spelleider, uitloggen, opnieuwLaden: laad } : null),
    [spelleider, uitloggen, laad],
  );

  if (stand.soort === 'laden') return <Wachten />;
  if (stand.soort === 'onbereikbaar') {
    return (
      <Center h="full" p="6">
        <VStack gap="4" textAlign="center" maxW="lg">
          <Heading as="h1" size="xl">
            Inloggen lukt nu niet
          </Heading>
          <Text>{stand.melding}</Text>
          <Button
            onClick={() => {
              setStand({ soort: 'laden' });
              void laad();
            }}
          >
            Opnieuw proberen
          </Button>
        </VStack>
      </Center>
    );
  }
  if (stand.soort === 'uit' || !sessie) {
    return (
      <Inlogscherm
        verlopen={stand.soort === 'uit' && stand.verlopen}
        onIngelogd={(ingelogd) => setStand({ soort: 'in', spelleider: ingelogd })}
      />
    );
  }
  return (
    <SessieContext value={sessie}>
      <Outlet />
    </SessieContext>
  );
}

/** Pas na een korte pauze een lader, zodat een snel antwoord niet knippert. */
function Wachten() {
  const [zichtbaar, setZichtbaar] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setZichtbaar(true), LADER_NA);
    return () => clearTimeout(timer);
  }, []);
  return zichtbaar ? <Laadscherm /> : null;
}
